/**
 * useUserPreferences — server-backed Light/Dark/System theme + Lite/Pro density
 * toggle for the admin shell.
 *
 * Source of truth is `GET /auth/me.preferences` (SCRUM-59). Mutations go through
 * `PUT /auth/me/preferences` with TanStack-optimistic update + revert on error.
 * Defaults apply only when the server has no row for the user.
 *
 * One-time backfill: users who had values in localStorage from SCRUM-58 (path A)
 * get those values written through to the server on first authenticated mount,
 * after which the localStorage entry is removed. The server then owns the value
 * exclusively.
 *
 * The public surface (`{ preferences, setTheme, setDensity }`) is unchanged from
 * the SCRUM-58 localStorage version — consumers (DashboardPage, AuthenticatedRootPage)
 * do not need to change.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { apiClient } from '../api-client.js';
import { useSession } from './useSession.js';

export type ThemePreference = 'light' | 'dark' | 'system';
export type DensityPreference = 'lite' | 'pro';

export interface UserPreferences {
  readonly theme: ThemePreference;
  readonly density: DensityPreference;
}

export interface UseUserPreferencesResult {
  readonly preferences: UserPreferences;
  setTheme: (theme: ThemePreference) => void;
  setDensity: (density: DensityPreference) => void;
}

/** Storage key — kept for backfill cleanup. After backfill completes the key is removed. */
export const PREFERENCES_STORAGE_KEY = 'sparta.admin.preferences';

/** Defaults applied when the server has no preferences row and localStorage is empty. */
export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  density: 'pro',
};

const VALID_THEMES: ReadonlySet<ThemePreference> = new Set([
  'light',
  'dark',
  'system',
]);
const VALID_DENSITIES: ReadonlySet<DensityPreference> = new Set(['lite', 'pro']);

function isValidTheme(value: unknown): value is ThemePreference {
  return typeof value === 'string' && VALID_THEMES.has(value as ThemePreference);
}

function isValidDensity(value: unknown): value is DensityPreference {
  return (
    typeof value === 'string' && VALID_DENSITIES.has(value as DensityPreference)
  );
}

interface SessionUserShape {
  preferences?: { theme?: unknown; density?: unknown } | null;
}

/**
 * Narrow the loosely-typed `useSession().user` to a server preferences object.
 * Returns `null` when the user hasn't set preferences yet or when the field
 * fails shape-validation (defensive against unexpected server responses).
 */
function extractServerPreferences(user: unknown): UserPreferences | null {
  if (typeof user !== 'object' || user === null) return null;
  const prefs = (user as SessionUserShape).preferences;
  if (prefs === null || prefs === undefined) return null;
  if (typeof prefs !== 'object') return null;
  if (!isValidTheme(prefs.theme) || !isValidDensity(prefs.density)) return null;
  return { theme: prefs.theme, density: prefs.density };
}

/**
 * Parse the legacy localStorage payload (written by the SCRUM-58 version of this
 * hook) into a `UserPreferences` value for backfill. Returns `null` when storage
 * is empty, unparseable, or holds out-of-enum values for BOTH fields — i.e. when
 * there is nothing meaningful to push to the server.
 */
function readLocalStorageForBackfill(): UserPreferences | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const theme = isValidTheme(obj.theme) ? obj.theme : null;
  const density = isValidDensity(obj.density) ? obj.density : null;
  if (theme === null && density === null) return null;
  return {
    theme: theme ?? DEFAULT_PREFERENCES.theme,
    density: density ?? DEFAULT_PREFERENCES.density,
  };
}

function clearLocalStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PREFERENCES_STORAGE_KEY);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        surface: 'admin-preferences',
        operation: 'localStorage.removeItem',
      },
      extra: { key: PREFERENCES_STORAGE_KEY },
    });
  }
}

const SESSION_QUERY_KEY = ['auth', 'me'] as const;

interface SessionCacheShape {
  readonly user?: SessionUserShape;
}

function applyOptimisticPreferences(
  cached: unknown,
  next: UserPreferences,
): unknown {
  if (typeof cached !== 'object' || cached === null) return cached;
  if (!('user' in cached)) return cached;
  const cache = cached as SessionCacheShape;
  if (!cache.user) return cache;
  return { ...cache, user: { ...cache.user, preferences: next } };
}

export function useUserPreferences(): UseUserPreferencesResult {
  const session = useSession();
  const queryClient = useQueryClient();
  const backfillFiredRef = useRef(false);

  const serverPreferences = extractServerPreferences(session.user);
  const preferences = serverPreferences ?? DEFAULT_PREFERENCES;

  const mutation = useMutation({
    mutationFn: async (next: UserPreferences): Promise<UserPreferences> => {
      const result = await apiClient.PUT('/auth/me/preferences', { body: next });
      if (!result.response.ok) {
        throw new Error(
          `PUT /auth/me/preferences failed with status ${result.response.status}`,
        );
      }
      return next;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: SESSION_QUERY_KEY });
      const previous = queryClient.getQueryData(SESSION_QUERY_KEY);
      queryClient.setQueryData(SESSION_QUERY_KEY, (cached: unknown) =>
        applyOptimisticPreferences(cached, next),
      );
      return { previous };
    },
    onError: (error, _next, context) => {
      if (context && 'previous' in context) {
        queryClient.setQueryData(SESSION_QUERY_KEY, context.previous);
      }
      Sentry.captureException(error, {
        tags: {
          surface: 'admin-preferences',
          operation: 'put-preferences',
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  // One-time backfill — runs at most once per mount, when the session resolves.
  useEffect(() => {
    if (backfillFiredRef.current) return;
    if (session.status !== 'authenticated') return;
    backfillFiredRef.current = true;

    if (serverPreferences !== null) {
      // Server is authoritative; legacy localStorage is obsolete.
      clearLocalStorage();
      return;
    }
    const legacy = readLocalStorageForBackfill();
    if (legacy === null) return;
    mutation.mutate(legacy, {
      onSuccess: () => clearLocalStorage(),
    });
  }, [session.status, serverPreferences, mutation]);

  const setTheme = useCallback(
    (theme: ThemePreference) => {
      mutation.mutate({ ...preferences, theme });
    },
    [preferences, mutation],
  );

  const setDensity = useCallback(
    (density: DensityPreference) => {
      mutation.mutate({ ...preferences, density });
    },
    [preferences, mutation],
  );

  return { preferences, setTheme, setDensity };
}
